
// A list of colors used for image hotspot markers.
// When editing, make sure to update the corresponding sass variables in hotspots.scss,
// (I still haven't found a way to unify these lists).
const hotspotColors = {
    red: "#f93446",
    teal: "#31d3c8",
    green: "#21db9c",
    blue: "#409fd7",
    lightblue: "#296bd7",
    navy: "#1d3f6a",
    yellow: "#ffe215",
    orange: "#fd7e14",
    pink: "#ffb7c4",
    purple: "#836ef1",
    white: "#ffffff",
    gray: "#dee2e6",
    tan: "#d1cdc5",
    transparent: "transparent",
}

// A mapping of glyphicons icon names to panel types.
const panelTypeIcons = {
    custom: "icon-glyphicons-basic-thumbnails",
    image: "icon-glyphicons-basic-picture",
    audio: "icon-glyphicons-basic-equalizer-dots",
    video: "icon-glyphicons-basic-square-triangle-right",
    pdf: "icon-glyphicons-basic-file",
    text: "icon-glyphicons-basic-paragraph-left",
    multiselect: "icon-glyphicons-basic-square-empty-check",
    singleselect: "icon-glyphicons-basic-circle-selected",
    multichoice: "icon-glyphicons-basic-directions-arrow",
    textarea: "icon-glyphicons-basic-square-edit",
    linked_response: "icon-glyphicons-basic-eye",
    vitals: "icon-glyphicons-basic-pulse",
    score: "icon-glyphicons-basic-medal",
    chat_v2: "icon-glyphicons-basic-chat-conversation",
    discovery: "icon-glyphicons-basic-tasks",
    step_order: "icon-glyphicons-basic-sort-numerically",
    "3d_video": "icon-glyphicons-basic-view-full",
    image_hotspots: "icon-glyphicons-basic-target",
    categories: "icon-glyphicons-basic-block-move",
    notepad: "icon-glyphicons-basic-notes",
    voice_recording: "icon-glyphicons-basic-mic",
    tabs: "icon-glyphicons-basic-modal-window",
    custom_form: "icon-glyphicons-basic-cheque",
    voice_conversation: "icon-glyphicons-basic-equalizer-bars",
}

const panelTypeLabels = {
    custom: "Combo Display",
    image: "Image",
    audio: "Audio",
    video: "Video",
    pdf: "PDF",
    text: "Text",
    multichoice: "Buttons",
    multiselect: "Multiple Selection",
    singleselect: "Multiple Choice",
    step_order: "Order the Steps",
    textarea: "Text Response",
    linked_response: "Review Response",
    score: "Learner Results",
    vitals: "Vitals Monitor",
    chat_v2: "Chat Conversation",
    discovery: "Discovered Item List",
    '3d_video': "360° Video",
    image_hotspots: "Interactive Image",
    categories: "Categories",
    notepad: "Notepad",
    voice_recording: "Voice Response",
    tabs: "Tabs",
    custom_form: "Custom Form",
    voice_conversation: "Voice Conversation",
}

// These settings match the primary and body colors defined within the scss variables
// file.
const primaryColor = '#115fde'
const bodyColor = '#3a3f44'

// Global theme color sets used to style the learner experience.
const globalThemeOptions = {
    bluenavy: {
        primary: "#115fde",
        secondary: "#304b6c",
        background: "#e9ecf2",
        alertBgLightPercent: 56,
        textDarkenPercent: 0,
    },
    tealnavy: {
        primary: "#09a99d",
        secondary: "#304b6c",
        background: "#e9ecf2",
        alertBgLightPercent: 58,
        textDarkenPercent: 8,
    },
    purple: {
        primary: "#836ef1",
        secondary: "#4a4a8c",
        background: "#eaedf1",
        alertBgLightPercent: 50,
        textDarkenPercent: 1.5,
    },
    green: {
        primary: "#14a775",
        secondary: "#226251",
        background: "#eaedf1",
        alertBgLightPercent: 62,
        textDarkenPercent: 10,
    },
}

// A lookup of human readable names for each panel type, and defines which panel types
// are allowed on an end node.
const panelTypes = [
    {
        value: "text",
        category: "display",
        description: "Add some text",
        paidOnly: false,
        beta: false,
        new: false,
        ai: false,
        allowPreContentVideo: true,
    },
    {
        value: "image",
        description: "Add an image",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: true,
        category: "display",
        defaultWidth: 500,
    },
    {
        value: "video",
        description: "Add a video",
        paidOnly: false,
        beta: false,
        new: false,
        ai: false,
        allowPreContentVideo: true,
        category: "display",
    },
    {
        value: "audio",
        description: "Add an audio clip",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: true,
        category: "display",
    },
    {
        value: "pdf",
        description: "Add a PDF or downloadable",
        paidOnly: false,
        beta: false,
        new: false,
        ai: false,
        allowPreContentVideo: true,
        category: "display",
        defaultWidth: 700,
    },
    {
        value: "tabs",
        description: "Clickable tabbed content",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "display",
    },
    {
        value: "custom",
        description: "Combine media types",
        paidOnly: false,
        beta: false,
        new: false,
        ai: false,
        allowPreContentVideo: true,
        category: "display",
    },
    {
        value: "3d_video",
        description: "Show a 360° video",
        paidOnly: false,
        beta: false,
        new: false,
        category: "display",
    },
    {
        value: "textarea",
        description: "Free-text entry",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: true,
        walkthroughVideoUrl: "https://vimeo.com/1151586942/f58367f1c4?autoplay=0",
        category: "interactive",
    },
    {
        value: "voice_conversation",
        description: "2-way realtime conversation",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: true,
        walkthroughVideoUrl: "https://vimeo.com/1152252670/b333246196?autoplay=0",
        category: "interactive",
    },
    {
        value: "voice_recording",
        description: "Microphone recording",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: true,
        walkthroughVideoUrl: "https://vimeo.com/1151933870/4d2f0917cf?autoplay=0",
        category: "interactive",
    },
    {
        value: "chat_v2",
        description: "Text-based conversation",
        paidOnly: false,
        beta: false,
        new: false,
        ai: true,
        allowPreContentVideo: false,
        category: "interactive",
        defaultWidth: 500,
    },
    {
        value: "multichoice",
        description: "Buttons with actions",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
        defaultWidth: 550,
    },
    {
        value: "image_hotspots",
        description: "2D or 360° interactive image",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        walkthroughVideoUrl: "https://vimeo.com/1153375321/3a4cd25c69?autoplay=0",
        category: "interactive",
        defaultWidth: 500,
    },
    {
        value: "multiselect",
        description: "Multi-select inquiry",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "singleselect",
        description: "Single-select inquiry",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "categories",
        description: "Category matching activity",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "custom_form",
        description: "Create a fillable form",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "step_order",
        description: "Draggable steps",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "notepad",
        description: "Display the notepad",
        paidOnly: false,
        beta: false,
        new: false,
        allowPreContentVideo: true,
        category: "interactive",
    },
    {
        value: "score",
        description: "Score & results summary",
        paidOnly: false,
        beta: false,
        new: false,
        category: "other",
    },
    {
        value: "linked_response",
        description: "Display a previous response",
        paidOnly: false,
        beta: false,
        new: false,
        category: "other",
    },
    {
        value: "discovery",
        description: "Display discovered items",
        paidOnly: false,
        beta: false,
        new: false,
        category: "other",
    },
    {
        value: "vitals",
        description: "Dynamic vital signs",
        paidOnly: false,
        beta: false,
        new: false,
        category: "other",
    },
]

const labelTranslations = {
    en: {
        continue: "Continue",
        submit: "Submit",
        responseSummary: "Response Summary",
        noResponse: "No Response Submitted",
        noResponses: "No Responses Submitted",
        yourChoices: "Your Choices",
        yourResponse: "Your Response",
        correctResponse: "Correct Response",
        typeYourResponse: "Type your response here...",
        editedResponse: "Edited Response",
        yourScore: "Your Score",
        close: "Close",
        successful: "Successful",
        unsuccessful: "Unsuccessful",
        explain: "Explain",
        downloadPDF: "Download PDF",
        chatConversation: "Chat Conversation",
        timeMetrics: "Time Performance",
        yourTime: "Your Time",
        correctCategory: "Correct Category",
        returnToLibrary: "Return to Library",
        returnToPage: "Return to Page",
        returnToStart: "Return to Start",
        done: "Done",
        restart: "Restart",
        resume: "Resume Session",
        sessionInProgress: "Session In Progress",
        resumeSessionMessage: "It looks like you currently have a session in progress for this learning experience.",
        closeBrowserTabs: "If you have this learning experience open in another browser tab, please close it now.",
        notepadEntries: "Notes",
        noNotes: "No notes to display.",
        notepad: "Notepad",
        resources: "Resources",
        openLearnerResources: "Learner Resources",
        openNotepad: "Notepad",
        requestSupport: "Help & Support",
        submitFeedback: "Submit Feedback",
        enterFullscreen: "Enter Fullscreen",
        exitFullscreen: "Exit Fullscreen",
        clickToAddress: "Click to address",
        replay: "Replay",
        back: "Back",
        showTranscript: "Show Transcript",
        gotIt: "Got It",
        skipTour: "Skip Tour",
        next: "Next",
        responseSaved: "Response saved",
        responseSaving: "Saving response",
        notesSaved: "Notes saved",
        notesSaving: "Saving notes",
        hotspotsExplored: "explored",
        lookAround: "Click and drag to look around",
        recordingMicrophoneTap: "Press on the microphone to start recording",
        recordingReview: "Review your recording",
        aiAudio: "The voice you are hearing was generated using AI.",
        scrollDown: "Scroll down to see more",
        analyzing: "Analyzing",
        compareYourAnswer: "Compare your response with the instructor's answer",
        aiGeneratedFeedback: "AI-Generated Feedback",
        analyzingYourResponse: "Analyzing your response",
        youConversationWillAppearHere: "Your conversation will appear here",
        saySomethingHere: "Say something here",
        continueBtnExp: {
            videos: "Videos must finish before continuing",
            activities: "Complete activities on this screen before continuing",
            unfinished: "You have unfinished activities on this screen that require completion",
        },
        submitBtnDisabledExp: "Complete activities on this screen before submitting your responses",
        instructorFeedback: "Instructor Feedback",
        silenceTours: "Silence all tours",
    },
    fr: {
        continue: "Continuer",
        submit: "Soumettre",
        responseSummary: "Résumé de votre performance",
        noResponse: "Aucune option sélectionnée",
        noResponses: "Aucune option sélectionnée",
        yourChoices: "Vos sélections",
        yourResponse: "Votre soumission",
        correctResponse: "Soumission correcte",
        typeYourResponse: "Tapez votre réponse ici...",
        editedResponse: "Soumission modifiée",
        yourScore: "Votre note",
        close: "Fermer",
        successful: "Terminé",
        unsuccessful: "Infructueux",
        explain: "Explique",
        downloadPDF: "Télécharger le PDF",
        chatConversation: "Conversation",
        timeMetrics: "Métriques de temps",
        yourTime: "Votre temps",
        correctCategory: "Bonne catégorie",
        returnToLibrary: "Retour à la bibliothèque",
        returnToPage: "Retour à la page",
        returnToStart: "Retourner au début",
        done: "Terminer",
        restart: "Recommencer",
        resume: "Reprendre votre session",
        sessionInProgress: "Séance en cours",
        resumeSessionMessage: "Il semble que vous ayez actuellement une session en cours pour cette expérience d'apprentissage.",
        closeBrowserTabs: "Si cette expérience d'apprentissage est ouverte dans un autre onglet de navigateur, veuillez la fermer maintenant.",
        notepadEntries: "Bloc-notes",
        noNotes: "Aucune note à afficher.",
        notepad: "Bloc-notes",
        resources: "Ressources",
        openLearnerResources: "Les ressources de l'apprenant",
        openNotepad: "Bloc-notes",
        requestSupport: "Aide et assistance",
        submitFeedback: "Soumettre des commentaires",
        enterFullscreen: "Passer en mode plein écran",
        exitFullscreen: "Quitter le mode plein écran",
        clickToAddress: "Cliquez pour adresser",
        replay: "Rejouer",
        back: "Retourner",
        showTranscript: "Montrer la transcription",
        gotIt: "J'ai compris",
        skipTour: "Passer le tour",
        next: "Procéder",
        responseSaved: "Réponse enregistrée",
        responseSaving: "Enregistrer la réponse",
        notesSaved: "Notes enregistrées",
        notesSaving: "Enregistrer des notes",
        hotspotsExplored: "explorés",
        lookAround: "Cliquez et faites glisser pour regarder autour de vous",
        recordingMicrophoneTap: "Cliquez ou appuyez sur le microphone pour démarrer l'enregistrement",
        recordingReview: "Vérifiez votre enregistrement",
        aiAudio: "La voix que vous entendez a été générée à l'aide de l'IA.",
        scrollDown: "Défiler vers le bas",
        analyzing: "En cours d'analyse",
        compareYourAnswer: "Comparez votre réponse avec celle de l'instructeur",
        aiGeneratedFeedback: "Commentaires générés par l'IA",
        analyzingYourResponse: "Analyser votre réponse",
        youConversationWillAppearHere: "Votre conversation apparaîtra ici",
        saySomethingHere: "Dis quelque chose ici",
        continueBtnExp: {
            videos: "Les vidéos doivent être terminées avant de continuer",
            activities: "Terminez les activités sur cet écran avant de continuer",
            unfinished: "Vous avez des activités inachevées sur cet écran qui doivent être complétées",
        },
        submitBtnDisabledExp: "Effectuez les activités sur cet écran avant de soumettre vos réponses",
        instructorFeedback: "Commentaires de l'instructeur",
        silenceTours: "Faites taire toutes les visites",
    }
}

const learnerTourStepTranslations = {
    en: {
        resources: {
            header: "Find learning resources here",
            content: "These resources will be available throughout the learning experience.",
        },
        notepad: {
            header: "Take notes here",
            content: "Your notepad will be available on every screen.",
        },
        comments: {
            header: "Add your comments here",
            content: "Add your comments & feedback to each screen as you move along.",
        },
        score: {
            header: "Your current score",
            content: "Your score will update as you collect (or lose) points.",
        },
        navigation: {
            header: "Navigate between screens",
            content: "Use this dropdown to navigate to any screen in the learning experience.",
        },
    },
    fr: {
        resources: {
            header: "Trouvez des ressources pour les apprenants ici",
            content: "Ces ressources seront disponibles tout au long de l'expérience d'apprentissage.",
        },
        notepad: {
            header: "Prenez des notes ici",
            content: "Votre bloc-notes sera disponible sur chaque écran.",
        },
        comments: {
            header: "Ajoutez vos commentaires ici",
            content: "Ajoutez vos commentaires et vos feedbacks à chaque écran au fur et à mesure de votre progression.",
        },
        score: {
            header: "Votre score actuel",
            content: "Votre score se mettra à jour au fur et à mesure que vous gagnerez (ou perdrez) des points.",
        },
        navigation: {
            header: "Naviguer entre les écrans",
            content: "Utilisez cette liste déroulante pour accéder à n'importe quel écran de l'expérience d'apprentissage.",
        },
    }    
}

const continueRequirementOptions = [
    {
        label: "No correct answers required to continue",
        value: "none",
    },
    {
        label: "At least one correct answer required to continue",
        value: "one",
    },
    {
        label: "All correct answers required to continue",
        value: "all",
    },
    {
        label: "Custom correct answer requirement",
        value: "custom",
    },
]

const roleNames = {
    student: "Learner",
    author: "Author",
    admin: "Admin",
}

const aiVoiceNames = [
    { label: "Coral", value: "coral" },
    { label: "Alloy", value: "alloy" },
    { label: "Ash", value: "ash" },
    { label: "Ballad", value: "ballad" },
    { label: "Echo", value: "echo" },
    { label: "Sage", value: "sage" },
    { label: "Nova", value: "nova" },
    { label: "Shimmer", value: "shimmer" },
    { label: "Verse", value: "verse" },
]

const ltiResourceLinkFormFields = [
    {
        name: "Learners will receive a grade for this activity",
        key: "enableGradePassback",
        type: "switch",
        subtext: "Turn this setting on to sync scores with your LMS.",
    },
    {
        name: "Allow grades to be overwritten after first attempt",
        key: "allowMultipleGradePassback",
        type: "switch",
        subtext: "When enabled, grades will be synced on each attempt.",
    },
    {
        name: "Allow multiple attempts",
        key: "allowMultipleAttempts",
        type: "switch",
        subtext: "Let learners restart and try again at any point in learning experience.",
    },
    {
        name: "Require manual grading by an instructor",
        key: "requireManualGrading",
        type: "switch",
        subtext: "Require that an instructor reviews grades before they're released to learners.",
        badge: "Canvas Only",
    },
]

const languageCodes = [
    { label: "Auto-Detect", value: "" },
    { label: "English", value: "en" },
    { label: "French", value: "fr" },
    { label: "Spanish", value: "es" },
    { label: "Chinese", value: "zh" },
    { label: "Arabic", value: "ar" },
    { label: "Hindi", value: "hi" },
    { label: "Urdu", value: "ur" },
    { label: "Portuguese", value: "pt" },
    { label: "German", value: "de" },
    { label: "Russian", value: "ru" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
]

export {
    primaryColor,
    bodyColor,
    globalThemeOptions,
    hotspotColors,
    panelTypeIcons,
    panelTypeLabels,
    panelTypes,
    learnerTourStepTranslations,
    labelTranslations,
    continueRequirementOptions,
    roleNames,
    aiVoiceNames,
    ltiResourceLinkFormFields,
    languageCodes,
}